<template>
  <nav class="navbar is-primary" role="navigation" aria-label="main navigation">
    <div class="navbar-brand">
      <RouterLink to="/" class="navbar-item">
        <strong>Beerview</strong>
      </RouterLink>

      <a
        role="button"
        class="navbar-burger"
        :class="{ 'is-active': isMobileMenuOpen }"
        aria-label="menu"
        aria-expanded="false"
        data-target="navbarMenu"
        @click="toggleMobileMenu"
      >
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
      </a>
    </div>

    <div
      id="navbarMenu"
      class="navbar-menu"
      :class="{ 'is-active': isMobileMenuOpen }"
    >
      <div class="navbar-start">
        <template v-if="authStore.isAuthenticated">
          <RouterLink
            to="/session-entry"
            class="navbar-item"
            @click="closeMobileMenu"
          >
            {{ t('header.nav.rateBeer') }}
          </RouterLink>
          <RouterLink
            to="/ratings"
            class="navbar-item"
            @click="closeMobileMenu"
          >
            {{ t('header.nav.ratings') }}
          </RouterLink>
          <RouterLink
            to="/sessions"
            class="navbar-item"
            @click="closeMobileMenu"
          >
            {{ t('header.nav.sessions') }}
          </RouterLink>
          <RouterLink to="/pubs" class="navbar-item" @click="closeMobileMenu">
            {{ t('header.nav.pubs') }}
          </RouterLink>
          <RouterLink
            to="/breweries"
            class="navbar-item"
            @click="closeMobileMenu"
          >
            {{ t('header.nav.breweries') }}
          </RouterLink>
          <RouterLink
            to="/styles"
            class="navbar-item"
            @click="closeMobileMenu"
          >
            {{ t('header.nav.styles') }}
          </RouterLink>

          <!-- Global Search -->
          <div class="navbar-item is-expanded">
            <GlobalSearch />
          </div>
        </template>
      </div>

      <div class="navbar-end">
        <!-- Locale Picker (visible to all users) -->
        <div class="navbar-item">
          <LocalePicker />
        </div>

        <template v-if="authStore.isAuthenticated">
          <!-- Admin links -->
          <template v-if="authStore.isAdmin">
            <div
              class="navbar-item has-dropdown is-hoverable"
              data-testid="admin-menu"
            >
              <a class="navbar-link">
                {{ t('header.admin.title') }}
              </a>
              <div class="navbar-dropdown">
                <RouterLink
                  to="/users"
                  class="navbar-item"
                  @click="closeMobileMenu"
                >
                  {{ t('header.admin.users') }}
                </RouterLink>
                <RouterLink
                  to="/administration"
                  class="navbar-item"
                  @click="closeMobileMenu"
                >
                  {{ t('header.admin.administration') }}
                </RouterLink>
                <RouterLink
                  to="/system-settings"
                  class="navbar-item"
                  @click="closeMobileMenu"
                >
                  {{ t('header.admin.systemSettings') }}
                </RouterLink>
                <RouterLink
                  to="/system-statistics"
                  class="navbar-item"
                  @click="closeMobileMenu"
                >
                  {{ t('header.admin.systemStatistics') }}
                </RouterLink>
              </div>
            </div>
          </template>

          <!-- User menu -->
          <div
            class="navbar-item has-dropdown is-hoverable"
            data-testid="user-menu"
          >
            <a class="navbar-link">
              {{ authStore.user?.username }}
            </a>
            <div class="navbar-dropdown">
              <RouterLink
                to="/my-statistics"
                class="navbar-item"
                data-testid="my-statistics-link"
                @click="closeMobileMenu"
              >
                {{ t('header.user.myStatistics') }}
              </RouterLink>
              <RouterLink
                to="/my-tastings"
                class="navbar-item"
                data-testid="my-quick-tastings-link"
                @click="closeMobileMenu"
              >
                {{ t('header.user.myQuickTastings') }}
              </RouterLink>
              <hr class="navbar-divider" />
              <a class="navbar-item" @click="handleLogout">
                {{ t('header.user.logout') }}
              </a>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="navbar-item">
            <div class="buttons">
              <RouterLink to="/login" class="button is-light">
                {{ t('header.login') }}
              </RouterLink>
            </div>
          </div>
        </template>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import GlobalSearch from '../search/GlobalSearch.vue'
import LocalePicker from '../LocalePicker.vue'

const authStore = useAuthStore()
const { t } = useI18n()
const isMobileMenuOpen = ref(false)

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const handleLogout = async () => {
  await authStore.logout()
  isMobileMenuOpen.value = false
}
</script>

<style scoped>
/* Search responsive behavior */
@media screen and (max-width: 1023px) {
  .navbar-item.is-expanded {
    padding: 0.5rem 0.75rem;
  }
}

@media screen and (min-width: 1024px) {
  .navbar-item.is-expanded {
    flex-grow: 1;
    max-width: 400px;
    margin-left: 1rem;
  }
}
</style>
